import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { LoginService } from '../../../services/login/login.service';
import { CVA_CONSTANTS } from '../../../util/constants';
import { ROUTE_CONSTANTS } from '../../../util/route.constants';
import { environment } from '../../../../environments/environment';

type AlertType = 'success' | 'error';

@Component({
    standalone: false,
    selector: 'cva-two-step-verification',
    templateUrl: './two-step-verification.component.html',
    styleUrl: './two-step-verification.component.scss',
    animations: [
        trigger('shake', [
            transition('void => false', []),
            transition('void => *, * => true', [
                animate('0.8s cubic-bezier(0.455, 0.03, 0.515, 0.955)', keyframes([
                    style({ transform: 'translate3d(0, 0, 0)', offset: 0 }),
                    style({ transform: 'translate3d(-10px, 0, 0)', offset: 0.1 }),
                    style({ transform: 'translate3d(10px, 0, 0)', offset: 0.2 }),
                    style({ transform: 'translate3d(-10px, 0, 0)', offset: 0.3 }),
                    style({ transform: 'translate3d(10px, 0, 0)', offset: 0.4 }),
                    style({ transform: 'translate3d(-10px, 0, 0)', offset: 0.5 }),
                    style({ transform: 'translate3d(10px, 0, 0)', offset: 0.6 }),
                    style({ transform: 'translate3d(-10px, 0, 0)', offset: 0.7 }),
                    style({ transform: 'translate3d(10px, 0, 0)', offset: 0.8 }),
                    style({ transform: 'translate3d(-10px, 0, 0)', offset: 0.9 }),
                    style({ transform: 'translate3d(0, 0, 0)', offset: 1 }),
                ])),
            ]),
        ]),
    ],
    
})
export class TwoStepVerificationComponent implements OnInit, OnDestroy {
  email = '';
  isVerifying = false;
  showAlert = false;
  alert: { type: AlertType; message: string } = {
    type: 'success',
    message: '',
  };
  routes_constants = ROUTE_CONSTANTS;
  cvaUI_url = environment.cvaUI_url;
  verificationCode: string[] = Array(6).fill('');
  codeArray = Array(6).fill(null);

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly loginService: LoginService,
  ) { }

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(
        filter(params => !!params['email']),
        takeUntil(this.destroy$)
      )
      .subscribe((params) => {
        this.email = params['email'];
      });

    if (!this.activatedRoute.snapshot.queryParams['email']) {
      this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  verifyOtp(): void {
    this.showAlert = false;

    const code = this.verificationCode.join('');

    if (code.length !== 6) {
      this.showErrorAlert('Please enter a valid 6-digit verification code.');
      return;
    }

    this.isVerifying = true;
    this.loginService.validateLoginOtp(this.email, code).subscribe({
      next: (res) => {
        const redirectUrl = res?.redirectUrl;
        if (redirectUrl) {
          this.loginService.loggedIn$.next(true);
          this.navigateToUrl(redirectUrl);
        } else {
          this.isVerifying = false;
          this.showErrorAlert('Unable to complete verification. Please try again.');
        }
      },
      error: (error) => {
        this.isVerifying = false;
        const errMsg = error?.error?.message || CVA_CONSTANTS.MESSAGE.UNKNOWN_LOGIN_ERR;
        this.showErrorAlert(errMsg);
      }
    });
  }

  onInput(event: Event, index: number): void {
    this.showAlert = false;

    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (!/^\d$/.test(value)) {
      input.value = '';
      return;
    }

    this.verificationCode[index] = value;

    if (value.length === 1 && index < 5) {
      const nextInput = input.nextElementSibling as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && index > 0) {
      const prevInput = input.previousElementSibling as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pastedData = event.clipboardData?.getData('text') || '';
    if (/^(\d\s*){6}$/.test(pastedData)) {
      this.verificationCode = pastedData.trim().replaceAll(' ', '').split('');
      const inputs = document.querySelectorAll('.digit-input');
      inputs.forEach((input, index) => {
        (input as HTMLInputElement).value = this.verificationCode[index];
      });
      event.preventDefault();
    } else {
      event.preventDefault();
    }
  }

  backToLogin(): void {
    this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
  }

  protected navigateToUrl(url: string): void {
    window.location.href = url;
  }

  private showErrorAlert(message: string): void {
    this.alert = {
      type: 'error',
      message,
    };
    this.showAlert = true;
  }
}
